import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import * as Sentry from '@sentry/angular';

const RELOAD_GUARD_KEY = '__zmbz_chunk_reload__';

const CHUNK_LOAD_PATTERNS = [
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	'ChunkLoadError',
	'Loading chunk',
	'Loading CSS chunk',
];

export function isChunkLoadError(error: unknown): boolean {
	if (!error) {
		return false;
	}

	const err = error as { name?: unknown; message?: unknown };
	const name = typeof err.name === 'string' ? err.name : '';
	const message = typeof err.message === 'string' ? err.message : '';
	const haystack = `${name} ${message}`;

	return CHUNK_LOAD_PATTERNS.some((pattern) => haystack.includes(pattern));
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	private readonly sentryHandler: ErrorHandler = Sentry.createErrorHandler({
		showDialog: false,
	});

	constructor(private readonly zone: NgZone) {}

	handleError(error: unknown): void {
		if (isChunkLoadError(error)) {
			this.recoverFromStaleDeploy(error);
			return;
		}

		this.sentryHandler.handleError(error);
	}

	private recoverFromStaleDeploy(error: unknown): void {
		this.sentryHandler.handleError(error);

		if (this.hasAlreadyReloaded()) {
			return;
		}

		this.markReloaded();

		this.zone.runOutsideAngular(() => {
			window.location.reload();
		});
	}

	private hasAlreadyReloaded(): boolean {
		try {
			return sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
		} catch {
			return false;
		}
	}

	private markReloaded(): void {
		try {
			sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
		} catch {
			// Storage disabled (private mode / WKWebView with cookies blocked):
			// we'll allow one reload per page load instead of one per session.
		}
	}
}
